import { type ReactNode, useMemo } from 'react';
import type { ContentCard } from '@/api/types';
import { CardCounters } from './CardCounters';

interface Props {
  card: ContentCard;
  onLike: () => void;
  onDislike: () => void;
  onBookmark: () => void;
  onComments: () => void;
  onShare: () => void;
  topSlot?: ReactNode;     // прогрессбар лимита/счётчик ветки и пр.
  bottomSlot?: ReactNode;  // что-то экстра под счётчиками
}

/**
 * Главная сущность — карточка контента.
 * Размер шрифта подстраивается под длину текста.
 * Единое оформление для quote и insight: слева, без обёрнутых кавычек.
 */
export function Card({ card, onLike, onDislike, onBookmark, onComments, onShare, topSlot, bottomSlot }: Props) {
  const text = card.text ?? '';

  // Авто-размер: чем длиннее текст, тем меньше шрифт.
  const fontSize = useMemo(() => {
    const len = text.length;
    if (len < 110) return 28;
    if (len < 200) return 24;
    return 22;
  }, [text]);

  return (
    <article className="relative h-full flex flex-col">
      {topSlot && <div className="px-4 pt-3">{topSlot}</div>}

      {/* Текст карточки */}
      <div className="flex-1 px-6 flex items-center">
        <p
          className="font-content text-text leading-snug"
          style={{ fontSize, fontWeight: 400 }}
        >
          {text}
        </p>
      </div>

      {/* Ambient strip — мягкая «дышащая» полоска под текстом */}
      <div className="px-6 mt-4 mb-1">
        <div
          className="relative h-[2px] w-full overflow-hidden rounded-pill"
          aria-hidden
          style={{ background: 'color-mix(in srgb, var(--text) 7%, transparent)' }}
        >
          <div className="card-strip-shimmer" />
        </div>
      </div>

      {/* Счётчики */}
      <div className="pt-2 pb-3">
        <CardCounters
          card={card}
          onLike={onLike} onDislike={onDislike} onBookmark={onBookmark}
          onComments={onComments} onShare={onShare}
        />
      </div>

      {/* Подпись с книгой — под действиями */}
      <div className="px-6 pb-4 text-center">
        <p className="text-xs" style={{ color: 'var(--text-muted)', opacity: 0.85 }}>
          {card.book.author} · {card.book.title}
        </p>
      </div>

      {bottomSlot && <div className="px-4 pb-3">{bottomSlot}</div>}
    </article>
  );
}
